import { useMemo, type ReactNode } from 'react';
import { resolveWidgetThumbnailComponent } from './widgetThumbnail.utils';

interface Props {
  /** Artifact widget key (e.g. `copy-generation-widget:v1`). */
  artifactKey: string | null | undefined;
  /** Render-data payload from the artifact detail. */
  data: unknown;
  /** Palette-icon (or other) fallback for unknown kinds / not-yet-loaded data. */
  fallback: ReactNode;
}

/**
 * Mounts the registry-bound widget for `artifactKey` in `'thumbnail'` mode —
 * the store-free, prop-only preview used by the Library grid card and the
 * picker tile. Falls back to `fallback` when the kind has no thumbnail
 * renderer or `data` has not loaded yet, so callers never crash on
 * unsupported kinds.
 */
export function WidgetThumbnail(props: Props) {
  const Widget = useMemo(
    () => resolveWidgetThumbnailComponent(props.artifactKey),
    [props.artifactKey],
  );

  if (!Widget || props.data == null) {
    return <>{props.fallback}</>;
  }

  // Widget thumbnails render as a centered white "page" floating on the card's
  // secondary block: `bg-white` pins a light surface (dark-mode legibility),
  // `w-3/5` + `justify-self-center` centers the page on the grey, and `p-3` is
  // the content inset. (The parent AssetCard block is a grid so justify-self
  // applies.)
  return (
    <div className="bg-white h-full w-3/5 justify-self-center p-3">
      <Widget data={props.data} mode="thumbnail" />
    </div>
  );
}
